import { useState, useEffect, useCallback } from 'react';
import { getUserProfile } from '../api/profile';
import { UserProfileData } from '../types/profile';
import { handleApiResponse } from '../utils/apiHelpers';
import { analyticsClient } from 'sdks/analytics';

interface UseUserProfileReturn {
    userProfile: UserProfileData | null;
    isLoading: boolean;
    error: string | null;
    hasError: boolean;
    refetch: () => void;
}

interface UseUserProfileOptions {
    enabled?: boolean;
}

export const useUserProfile = (options?: UseUserProfileOptions): UseUserProfileReturn => {
    const { enabled = true } = options || {};
    const [userProfile, setUserProfile] = useState<UserProfileData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchUserProfile = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await getUserProfile();

            const result = handleApiResponse<UserProfileData>(
                response,
                (data) => {
                    analyticsClient.setUser(data.id);
                    setUserProfile(data);
                },
                (error) => {
                    analyticsClient.setUser(null);
                    setError(error);
                }
            );

            if (!result.success) {
                return;
            }
        } catch (err) {
            setError('Failed to fetch user profile');
            console.error('Failed to fetch user profile:', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (enabled) {
            fetchUserProfile();
        }
    }, [enabled, fetchUserProfile]);

    return {
        userProfile,
        isLoading,
        error,
        hasError: !!error,
        refetch: fetchUserProfile
    };
};
